import React, { useState, useRef, useEffect } from "react";
import Transition from "../utils/Transition";
import moment from "moment";
import ModalBasic from "./ModalBasic.jsx";
import Datepicker from "./Datepicker.jsx";

function DateSelect({ onSelect = (p) => {}, defaultSelected = 2 }) {
  const [startDate, setStartDate] = useState(() => {
    if (defaultSelected === 8) {
      return new Date(0); // All Time
    }
    return new Date().setDate(new Date().getDate() - 7);
  });
  const [endDate, setEndDate] = useState(() => {
    if (defaultSelected === 8) {
      return new Date(); // All Time
    }
    return new Date().setDate(new Date().getDate() - 1);
  });

  const [showCustomModal, setShowCustomModal] = useState(false);

  const options = [
    {
      id: 0,
      period: "Today",
    },
    {
      id: 1,
      period: "Yesterday",
    },
    {
      id: 2,
      period: "Last 7 Days",
    },
    {
      id: 3,
      period: "Last 30 Days",
    },
    {
      id: 4,
      period: "Month to Date",
    },
    {
      id: 5,
      period: "Last Calendar Month",
    },
    {
      id: 6,
      period: "Year to Date",
    },
    {
      id: 7,
      period: "Last 12 Months",
    },
    {
      id: 8,
      period: "All Time",
    },
    {
      id: 9,
      period: "Custom Range",
    },
  ];

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(defaultSelected);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Trigger initial date selection on mount
  useEffect(() => {
    if (defaultSelected === 8) {
      onSelect({
        start: moment(new Date(0)).format("YYYY-MM-DD"),
        end: moment(new Date()).format("YYYY-MM-DD")
      });
    }
  }, []);

  useEffect(() => {
    let start = new Date();
    let end = new Date();

    if (selected === 9) {
      return;
    }

    switch (selected) {
      case 0:
        start = new Date();
        end = new Date();
        break;
      case 1:
        start = new Date();
        end = new Date();
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 2:
        start = new Date();
        end = new Date();
        start.setDate(start.getDate() - 7);
        break;
      case 3:
        start = new Date();
        end = new Date();
        start.setDate(start.getDate() - 30);
        break;
      case 4:
        start = new Date(start.getFullYear(), start.getMonth(), 1);
        end = new Date();
        break;
      case 5:
        start = new Date(start.getFullYear(), start.getMonth() - 1, 1);
        end = new Date(end.getFullYear(), end.getMonth(), 0);
        break;
      case 6:
        start = new Date(start.getFullYear(), 0, 1);
        end = new Date();
        break;
      case 7:
        start = new Date(start.getFullYear() - 1, start.getMonth(), 1);
        end = new Date();
        break;
      case 8:
        start = new Date(0);
        end = new Date();
        break;
      case 9:
        start = new Date();
        end = new Date();
        break;
      default:
        break;
    }
    // YYYY-MM-DD
    start = moment(start).format("YYYY-MM-DD");
    end = moment(end).format("YYYY-MM-DD");
    onSelect({ start, end });
  }, [selected]);
  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <div className="relative">
      <ModalBasic
        width={"sm"}
        modalOpen={showCustomModal}
        setModalOpen={setShowCustomModal}
        title={"Custom Date Range"}
      >
        <div className="overflow-hidden flex justify-center">
          <Datepicker
            defaultOpen={true}
            align="center"
            onChange={(props) => {
              if (
                props.length !== 2 ||
                props[0].getTime() === props[1].getTime()
              ) {
                return;
              }
              setStartDate(props[0].getTime());
              setEndDate(props[1].getTime());
              onSelect({
                start: moment(props[0]).format("YYYY-MM-DD"),
                end: moment(props[1]).format("YYYY-MM-DD"),
              });
              setShowCustomModal(false);
            }}
          />
        </div>
      </ModalBasic>
      <button
        ref={trigger}
        className="btn justify-between min-w-60 bg-white dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-500 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-200"
        aria-label="Select date range"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center">
          <svg
            className="w-4 h-4 fill-current text-slate-500 dark:text-slate-400 shrink-0 mr-2"
            viewBox="0 0 16 16"
          >
            <path d="M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1zm-1 12H2V6h12v8z" />
          </svg>
          <span>
            {selected !== 9 && options[selected].period}
            {selected === 9 &&
              `${moment(startDate).format("MMM D, YYYY")} - ${moment(
                endDate
              ).format("MMM D, YYYY")}`}
          </span>
        </span>
        <svg
          className="shrink-0 ml-1 fill-current text-slate-400"
          width="11"
          height="7"
          viewBox="0 0 11 7"
        >
          <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
        </svg>
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className="z-10 absolute top-full right-0 w-full bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="font-medium text-sm text-slate-600 dark:text-slate-300"
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          {options.map((option) => {
            return (
              <button
                key={option.id}
                tabIndex="0"
                className={`flex items-center w-full hover:bg-slate-50 hover:dark:bg-slate-700/20 py-1 px-3 cursor-pointer ${
                  option.id === selected && "text-indigo-500"
                }`}
                onClick={() => {
                  if (option.id === 9) {
                    setShowCustomModal(true);
                  }
                  setSelected(option.id);
                  setDropdownOpen(false);
                }}
              >
                <svg
                  className={`shrink-0 mr-2 fill-current text-indigo-500 ${
                    option.id !== selected && "invisible"
                  }`}
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                >
                  <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                </svg>
                <span>{option.period}</span>
              </button>
            );
          })}
        </div>
      </Transition>
    </div>
  );
}

export default DateSelect;
